import { getPromozavrRequestKey, shouldInitPromozavr } from '../../../../../common/helpers';
import { isDarkTheme } from '../../helpers/theme';
import type { PromozavrRendererSource, PromozavrState } from './types';

type ComputePromozavrRuntimeDecisionArgs = {
    pathname: string;
    search: string;
    isYandexNet: boolean;
    shouldUsePaas: boolean;
    shouldUsePdokPromoRenderer: boolean;
    isMessenger: boolean;
    isMeetingRoom: boolean;
};

type ShouldDispatchPromozavrInitArgs = {
    shouldInit: boolean;
    requestKey?: string;
    nextRequestKey: string;
};

type PromozavrDebugSnapshotArgs = Pick<
    PromozavrState,
    | 'status'
    | 'requestKey'
    | 'request'
    | 'selectedPromo'
    | 'translations'
    | 'images'
    | 'isSelected'
    | 'rendererSource'
    | 'lastError'
> & {
    pathname: string;
    shouldInit: boolean;
};

type BuildPromozavrRuntimeViewStateArgs = Pick<
    PromozavrState,
    'requestKey' | 'selectedPromo' | 'translations' | 'isSelected'
> &
    ComputePromozavrRuntimeDecisionArgs & {
        bodyThemeName: string | null;
    };

type ShouldResetPromozavrStateArgs = Pick<
    PromozavrState,
    | 'status'
    | 'request'
    | 'requestKey'
    | 'selectedPromo'
    | 'translations'
    | 'images'
    | 'isSelected'
    | 'rendererSource'
    | 'lastError'
> & {
    shouldInit: boolean;
    nextRendererSource: PromozavrRendererSource;
};

type PromozavrDebugWindowLike = {
    __telemostPromozavr?: Record<string, unknown>;
};

export function computePromozavrRuntimeDecision(
    {
        pathname,
        search,
        isYandexNet,
        shouldUsePaas,
        shouldUsePdokPromoRenderer,
        isMessenger,
        isMeetingRoom,
    }: ComputePromozavrRuntimeDecisionArgs,
    resolveRendererSource: (shouldUseRemoteRenderer: boolean) => PromozavrRendererSource
) {
    const hasDebugQuery = isYandexNet && new URLSearchParams(search).has('promozavr');
    const shouldInit = shouldInitPromozavr({
        isPaasEnabled: shouldUsePaas,
        pathname,
        isMessenger,
        isMeetingRoom,
    });
    const nextRequestKey = getPromozavrRequestKey(pathname, search);
    const nextRendererSource = resolveRendererSource(shouldInit && shouldUsePdokPromoRenderer);

    return {
        hasDebugQuery,
        shouldInit,
        nextRequestKey,
        nextRendererSource,
    };
}

export function buildPromozavrRuntimeViewState(
    {
        bodyThemeName,
        requestKey,
        selectedPromo,
        translations,
        isSelected,
        ...runtimeDecisionArgs
    }: BuildPromozavrRuntimeViewStateArgs,
    resolveRendererSource: (shouldUseRemoteRenderer: boolean) => PromozavrRendererSource
) {
    const runtimeDecision = computePromozavrRuntimeDecision(runtimeDecisionArgs, resolveRendererSource);

    return {
        ...runtimeDecision,
        isDark: Boolean(isDarkTheme(bodyThemeName)),
        shouldRender: Boolean(
            runtimeDecision.shouldInit &&
                requestKey === runtimeDecision.nextRequestKey &&
                isSelected &&
                selectedPromo &&
                translations
        ),
    };
}

export function shouldDispatchPromozavrInit({
    shouldInit,
    requestKey,
    nextRequestKey,
}: ShouldDispatchPromozavrInitArgs) {
    return shouldInit && requestKey !== nextRequestKey;
}

export function shouldResetPromozavrState({
    shouldInit,
    status,
    request,
    requestKey,
    selectedPromo,
    translations,
    images,
    isSelected,
    rendererSource,
    lastError,
    nextRendererSource,
}: ShouldResetPromozavrStateArgs) {
    if (shouldInit) {
        return false;
    }

    return (
        status !== 'idle' ||
        request !== undefined ||
        requestKey !== undefined ||
        selectedPromo !== undefined ||
        translations !== undefined ||
        images !== undefined ||
        isSelected ||
        rendererSource !== nextRendererSource ||
        lastError !== undefined
    );
}

export function buildPromozavrDebugSnapshot({
    pathname,
    shouldInit,
    status,
    requestKey,
    request,
    selectedPromo,
    translations,
    images,
    isSelected,
    rendererSource,
    lastError,
}: PromozavrDebugSnapshotArgs) {
    return {
        pathname,
        shouldInit,
        status,
        requestKey,
        request,
        response: {
            selectedPromos: selectedPromo ? [selectedPromo] : [],
            translations,
            images,
            isSelected,
        },
        selectedPromo,
        rendererSource,
        lastError,
    };
}

export function syncPromozavrDebugSnapshot(
    windowLike: PromozavrDebugWindowLike,
    snapshot: Record<string, unknown>,
    enabled: boolean
) {
    if (!enabled) {
        delete windowLike.__telemostPromozavr;
        return;
    }

    windowLike.__telemostPromozavr = snapshot;
}

export function normalizePromozavrUrl(host: string) {
    const normalizedHost = host.replace(/\/+$/, '');

    if (normalizedHost.startsWith('http://') || normalizedHost.startsWith('https://')) {
        return normalizedHost;
    }

    return `https://${normalizedHost}`;
}

export function getPromozavrTuningUrl(yandex360Host: string) {
    return normalizePromozavrUrl(yandex360Host);
}

export function buildPromozavrGetNow(serverTime?: number) {
    const startedAt = Date.now();
    const delta = typeof serverTime === 'number' ? serverTime - startedAt : 0;

    return () => Date.now() + delta;
}
